import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { cn } from 'lib/utils';
import config from 'config';
import { Button } from 'components/ui/button';
import { useTelegramBotLogin, type TelegramBotLoginPhase } from 'modules/auth';
import { socialButtonClassName } from './socialButton';
import { TelegramLogo } from './TelegramLogo';

interface TelegramBotLoginProps {
  accountType?: 'STAFF' | 'CANDIDATE';
  /** 'login' = the Login page: approving in the bot must not create an account. */
  mode?: 'login' | 'signup';
  /** Internal path to land on after a successful sign-in. */
  redirectTo?: string;
  /** Optional custom button label */
  label?: string;
  children?: React.ReactNode;
  /** Optional badge text displayed on the right (e.g. "Tezkor") */
  badge?: string;
  /** Callback fired whenever the bot login phase transitions */
  onPhaseChange?: (phase: TelegramBotLoginPhase) => void;
  /** Extra classes for the outer container. */
  className?: string;
}

/** Bot-approve login: no Login Widget, no /setdomain. The bot username is only an on/off signal. */
export const TelegramBotLogin: React.FC<TelegramBotLoginProps> = ({
  redirectTo = '/app',
  accountType,
  mode,
  label,
  children,
  badge,
  onPhaseChange,
  className,
}) => {
  const enabled = Boolean(config.telegram.bot_username);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { phase, deepLink, start, reset } = useTelegramBotLogin(accountType, mode);

  React.useEffect(() => {
    onPhaseChange?.(phase);
  }, [phase, onPhaseChange]);

  React.useEffect(() => {
    if (phase === 'approved') {
      navigate(redirectTo, { replace: true });
    }
  }, [phase, navigate, redirectTo]);

  if (!enabled) return null;

  // Idle / starting → the entry button.
  if (phase === 'idle' || phase === 'starting') {
    return (
      <div className={cn('flex justify-center w-full', className)}>
        <button type="button" className={socialButtonClassName} onClick={start} disabled={phase === 'starting'}>
          {phase === 'starting' ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
          ) : (
            <TelegramLogo className="h-[18px] w-[18px] shrink-0" />
          )}
          <span>{children ?? label ?? t('auth.continue_with_telegram')}</span>
          {badge && (
            <span className="ml-auto rounded-md bg-muted px-2 py-0.5 text-[11px] font-medium text-foreground/80">{badge}</span>
          )}
        </button>
      </div>
    );
  }

  // No account for this Telegram identity → send them to signup; retrying can't help.
  if (phase === 'no_account') {
    return (
      <div
        className={cn(
          'flex w-full flex-col items-center gap-3.5 rounded-[10px] border border-error-200 bg-error-50 p-4 text-center dark:border-error-900/50 dark:bg-error-900/20',
          className,
        )}
      >
        <p role="alert" className="text-sm font-medium text-error-700 dark:text-error-400">
          {t('auth.no_account_signup')}
        </p>
        <div className="flex w-full items-center gap-2">
          <Link to="/signup" className={cn(socialButtonClassName, 'h-10 flex-1')}>
            {t('auth.create_account')}
          </Link>
          <Button type="button" variant="ghost" className="h-10 text-sm text-muted-foreground hover:text-foreground" onClick={reset}>
            {t('cancel')}
          </Button>
        </div>
      </div>
    );
  }

  // Terminal non-success → message + retry.
  if (phase === 'rejected' || phase === 'expired' || phase === 'error') {
    const message =
      phase === 'rejected'
        ? t('auth.telegram_rejected')
        : phase === 'expired'
          ? t('auth.telegram_expired')
          : t('auth.telegram_failed');
    return (
      <div
        className={cn(
          'flex w-full flex-col items-center gap-3.5 rounded-[10px] border border-error-200 bg-error-50 p-4 text-center dark:border-error-900/50 dark:bg-error-900/20',
          className,
        )}
      >
        <p role="alert" className="text-sm font-medium text-error-700 dark:text-error-400">
          {message}
        </p>
        <div className="flex w-full items-center gap-2">
          <button type="button" className={cn(socialButtonClassName, 'h-10 flex-1')} onClick={start}>
            <TelegramLogo className="h-4 w-4 shrink-0" />
            {t('auth.telegram_try_again')}
          </button>
          <Button type="button" variant="ghost" className="h-10 text-sm text-muted-foreground hover:text-foreground" onClick={reset}>
            {t('cancel')}
          </Button>
        </div>
      </div>
    );
  }

  // waiting / approved → the clean full-width approval panel.
  return (
    <div
      className={cn(
        'flex w-full flex-col items-center gap-3.5 rounded-[10px] border border-border bg-card p-5 text-center',
        className,
      )}
    >
      <TelegramLogo className="h-9 w-9" />

      <div className="space-y-1">
        <p className="text-[15px] font-semibold text-foreground">{t('auth.telegram_approve_title')}</p>
        <p className="mx-auto max-w-xs text-[13px] leading-relaxed text-muted-foreground">{t('auth.telegram_approve_desc')}</p>
      </div>

      {deepLink && (
        <a
          href={deepLink}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full"
        >
          {/* Telegram's own blue: this button hands off to the Telegram app. */}
          <Button type="button" className="h-11 w-full gap-2 bg-[#229ED9] text-white hover:bg-[#1E8EC4]">
            <span>{t('auth.telegram_open_app')}</span>
          </Button>
        </a>
      )}

      <div className="flex items-center justify-center gap-2 text-xs font-medium text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>{t('auth.telegram_waiting')}</span>
      </div>

      <button
        type="button"
        onClick={reset}
        className="text-xs font-medium text-muted-foreground hover:text-foreground transition-colors pt-0.5"
      >
        {t('cancel')}
      </button>
    </div>
  );
};

export default TelegramBotLogin;
